/**
 * Panneau « Scribe » du dock (bouton Scribe de DockAgora, panneau Radix du dock : Échap ou clic à côté pour fermer, focus
 * rendu au bouton). Selon l'état de l'enregistreur (Scribe.tsx) :
 *  - avant : titre de la réunion, niveau du compte-rendu (Essentiel, Détaillé par défaut, Exhaustif), choix du micro et
 *    son niveau, option « Participants à distance (son de Teams) » (Edge, Chrome : partage de l'onglet ou de l'écran de
 *    Teams avec son audio, à faire avant de démarrer), rappel d'informer les participants et la case « Participants
 *    informés » (obligatoire), temps enregistré de la semaine sur le plafond, « Démarrer l'enregistrement » ;
 *    compte-rendu dans la langue d'AGORA de l'utilisateur ; lot 3 « agenda Outlook » : réunion Outlook en cours ou qui
 *    commence dans les 15 minutes (« Réunion Outlook : sujet (10:30 – 11:30, participants : n) », « Utiliser » : titre
 *    = sujet, invités joints à la réunion démarrée ; plusieurs : une ligne chacune) ; titre laissé par défaut : l'IA en
 *    proposera un (`titreAuto`) ;
 *  - pendant : durée, « Scribe prend des notes » (ou « Hors ligne · Parties en attente : n », ou « En pause » après
 *    60 s de silence, avec « Reprendre maintenant »), micro (changeable : nouvelle séquence) et son niveau, partage du son
 *    de Teams (terminé : avis et « Partager de nouveau »), « Arrêter l'enregistrement » ;
 *  - après : envoi des dernières parties, puis « Traitement » et le lien vers la réunion (écran Outils › Scribe) ;
 *  - réunion inachevée (rechargement, plantage, micro débranché) : « Reprendre » ou « Terminer » ; si elle enregistrait
 *    le son de Teams, l'option revient cochée et le partage est à refaire ;
 *  - autre onglet qui enregistre (état diffusé par celui-ci, onglets.ts) : titre de la réunion, durée et état (« Scribe
 *    prend des notes », « En pause », hors ligne), « Arrêter l'enregistrement » (confirmation) transmis à cet onglet, qui
 *    s'arrête comme si l'on avait cliqué chez lui ; état inconnu (navigateur sans canal entre onglets, envoi des
 *    dernières parties) : « Scribe enregistre dans un autre onglet ».
 * Le micro n'est ouvert que panneau ouvert (ou pendant l'enregistrement) : le fermer avant de démarrer rend le micro.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BORNES_SCRIBE, DUREE_MAX_REUNION_S, NIVEAU_COMPTE_RENDU_DEFAUT, NIVEAUX_COMPTE_RENDU, RESSOURCE_SCRIBE, ROUTE_SCRIBE, schemaReunionNouvelle,
  type AgendaScribe, type EvenementAgenda, type InviteReunion, type QuotaScribe,
} from '@agora/shared';
import { Button, Icon, SegmentedControl, SelectField, Skeleton, SkeletonGroup, StatusBadge, TextField, Toggle, cx, type Status } from '../../design';
import { useFormats } from '../../socle';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import { useActionsEspace } from '../pages/espace';
import { useConfirmation } from '../retours/Retours';
import { Formulaire, useFormulaire } from '../formulaires/formulaire';
import { dureeAffichee, dureeLisible, formaterChrono, type EtatScribe } from './enregistreur';
import { useScribe, type ContexteScribe } from './Scribe';

/** Champ des participants (TokenField, Downshift) : chargé à l'ouverture de la section, hors du premier chargement. */
const ChampParticipants = React.lazy(() => import('./ChampParticipants'));
/** Adresse d'un participant bien formée (le schéma partagé la revérifie) : une adresse en erreur n'est pas reprise. */
const ADRESSE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

/** Mesure du niveau du micro : 10 fois par seconde. */
const MESURE_NIVEAU_MS = 100;

/** Durée affichée (« 00:42:15 »), rafraîchie chaque seconde pendant l'enregistrement. */
export function useChrono(etat: EtatScribe | undefined): string {
  const actif = etat?.phase === 'enregistrement';
  const [maintenant, setMaintenant] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!actif) return;
    setMaintenant(Date.now());
    const id = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [actif]);
  return etat ? formaterChrono(dureeAffichee(etat, maintenant)) : '';
}

type CleEtatPanneau = 'pret' | 'enregistrement' | 'pause' | 'horsLigne' | 'envoi' | 'traitement' | 'aReprendre' | 'ailleurs';

/** Badge d'un enregistrement en cours (dans cet onglet ou dans un autre). */
function etatEnregistrement(e: EtatScribe): { status: Status; cle: CleEtatPanneau } {
  if (e.horsLigne) return { status: 'waiting', cle: 'horsLigne' };
  return e.pause ? { status: 'waiting', cle: 'pause' } : { status: 'danger', cle: 'enregistrement' };
}

/**
 * Enregistrement d'un autre onglet à montrer ici : cet onglet n'enregistre pas et n'envoie rien (comme le bouton du
 * dock ; sa réunion précédente en traitement reste dans Outils › Scribe).
 */
function enregistrementAilleurs(s: ContexteScribe): EtatScribe | null {
  const { phase } = s.etat;
  return s.ailleurs && phase !== 'enregistrement' && phase !== 'envoi' && s.autreOnglet?.phase === 'enregistrement' ? s.autreOnglet : null;
}

/** Badge d'état du panneau. */
function etatPanneau(s: ContexteScribe): { status: Status; cle: CleEtatPanneau } {
  const { phase, horsLigne } = s.etat;
  const autre = enregistrementAilleurs(s);
  if (autre) return etatEnregistrement(autre);
  if (s.ailleurs && (phase === 'inactif' || phase === 'a-reprendre')) return { status: 'neutral', cle: 'ailleurs' };
  if (phase === 'enregistrement') return etatEnregistrement(s.etat);
  if (phase === 'envoi') return horsLigne ? { status: 'waiting', cle: 'horsLigne' } : { status: 'progress', cle: 'envoi' };
  if (phase === 'traitement') return { status: 'success', cle: 'traitement' };
  if (phase === 'a-reprendre') return { status: 'waiting', cle: 'aReprendre' };
  return { status: 'neutral', cle: 'pret' };
}

export function PanneauScribe({ fermer }: { fermer: () => void }) {
  const { t, lib } = useT();
  const s = useScribe();
  const espace = useActionsEspace();
  const chrono = useChrono(s?.etat);
  const chronoAutre = useChrono(s?.autreOnglet ?? undefined);

  // Micro ouvert tant que le panneau l'est (niveau, démarrage), rendu à la fermeture hors enregistrement.
  const besoinMicro = !!s && !s.ailleurs && !!s.format && (s.etat.phase === 'inactif' || s.etat.phase === 'a-reprendre');
  const flux = s?.micro.flux ?? null;
  const erreurMicro = s?.micro.erreur ?? null;
  const ouvrirMicro = s?.micro.ouvrir;
  const choisi = s?.micro.choisi;
  React.useEffect(() => {
    if (besoinMicro && !flux && !erreurMicro) void ouvrirMicro?.(choisi || undefined);
  }, [besoinMicro, flux, erreurMicro, ouvrirMicro, choisi]);
  const fermerMicro = React.useRef(s?.micro.fermer);
  fermerMicro.current = s?.micro.fermer;
  React.useEffect(() => () => fermerMicro.current?.(), []);

  if (!s) return null;
  const { etat } = s;
  const titre = lib(RESSOURCE_SCRIBE);
  const badge = etatPanneau(s);
  const mesReunions = (
    <Button variant="quiet" size="sm" icon="list" onClick={(e) => { espace.ouvrir(ROUTE_SCRIBE, { evenement: e }); fermer(); }}>
      {t('outils.scribe.panneau.mesReunions')}
    </Button>
  );

  const autre = enregistrementAilleurs(s);

  let contenu: React.ReactNode;
  if (autre) {
    contenu = <><EnregistrementAilleurs autre={autre} chrono={chronoAutre} arreter={s.arreterAutreOnglet} />{mesReunions}</>;
  } else if (badge.cle === 'ailleurs') {
    contenu = <><p className="agx-vide">{t('outils.scribe.panneau.ailleurs')}</p>{mesReunions}</>;
  } else if (!s.format && (etat.phase === 'inactif' || etat.phase === 'a-reprendre')) {
    contenu = <><p className="agx-scribe-erreur" role="alert">{t('outils.scribe.panneau.formatAbsent')}</p>{mesReunions}</>;
  } else if (etat.phase === 'enregistrement') {
    contenu = (
      <>
        <LigneChrono etat={etat} chrono={chrono} />
        {etat.horsLigne ? <p className="agx-vide">{t('outils.scribe.panneau.horsLigneAide')}</p> : null}
        {etat.pause ? (
          <div className="agx-scribe-pause" role="status">
            <p>{t('outils.scribe.panneau.pauseAide')}</p>
            <Button size="sm" icon="micro" onClick={s.reprendreSon}>{t('outils.scribe.panneau.reprendreMaintenant')}</Button>
          </div>
        ) : null}
        {s.avisSon ? <div className="agx-message agx-message-info" role="status"><span>{t(`outils.scribe.panneau.avisSon.${s.avisSon}`)}</span></div> : null}
        <ChoixMicro s={s} />
        <OptionDistance s={s} pendant />
        <p className="agx-vide">{t('outils.scribe.panneau.garderOnglet')}</p>
        {s.persistant ? null : <p className="agx-vide">{t('outils.scribe.panneau.sansReprise')}</p>}
        <div className="agx-scribe-actions">
          <Button variant="danger" onClick={s.arreter}>{t('outils.scribe.panneau.arreter')}</Button>
        </div>
      </>
    );
  } else if (etat.phase === 'envoi') {
    contenu = <p>{etat.horsLigne ? t('outils.scribe.panneau.envoiHorsLigne', { n: etat.enAttente }) : t('outils.scribe.panneau.envoi')}</p>;
  } else if (etat.phase === 'traitement') {
    const id = etat.reunion?.id;
    contenu = (
      <>
        <p>{t('outils.scribe.panneau.traitement')}</p>
        <div className="agx-scribe-actions">
          {id ? (
            <Button variant="primary" icon="external" onClick={(e) => { espace.ouvrir(`${ROUTE_SCRIBE}/${encodeURIComponent(id)}`, { evenement: e }); fermer(); }}>
              {t('outils.scribe.panneau.voirReunion')}
            </Button>
          ) : null}
          <Button variant="quiet" onClick={s.nouvelle}>{t('outils.scribe.panneau.nouvelle')}</Button>
        </div>
      </>
    );
  } else if (etat.phase === 'a-reprendre') {
    contenu = (
      <>
        <p>{t('outils.scribe.panneau.aReprendre', { titre: etat.reunion?.titre ?? '', duree: chrono })}</p>
        {etat.enAttente ? <p className="agx-vide">{t('outils.scribe.panneau.aReprendreAttente', { n: etat.enAttente })}</p> : null}
        <ChoixMicro s={s} />
        <OptionDistance s={s} />
        <div className="agx-scribe-actions">
          <Button variant="primary" icon="micro" disabled={!s.micro.flux || attenteDistance(s)} onClick={() => void s.reprendre()}>{t('outils.scribe.panneau.reprendre')}</Button>
          <Button onClick={s.terminer}>{t('outils.scribe.panneau.terminer')}</Button>
        </div>
      </>
    );
  } else {
    contenu = <><Demarrage s={s} />{mesReunions}</>;
  }

  return (
    <section className={cx('agx-scribe-panneau', 'ag-glass', 'ag-glass-strong')} role="dialog" aria-label={titre}>
      <header className="agx-scribe-entete">
        <h2>{titre}</h2>
        <StatusBadge status={badge.status} label={t(`outils.scribe.panneau.etats.${badge.cle}`)} />
        <Button variant="quiet" size="sm" icon="close" aria-label={t('commun.fermer')} onClick={fermer} />
      </header>
      {etat.avis ? <div className="agx-message agx-message-info" role="alert"><span>{t(`outils.scribe.panneau.avis.${etat.avis}`, { h: DUREE_MAX_REUNION_S / 3600 })}</span></div> : null}
      {contenu}
    </section>
  );
}

/** Durée de l'enregistrement, point rouge (fixe en pause) et état : prend des notes, en pause, hors ligne. */
function LigneChrono({ etat, chrono }: { etat: EtatScribe; chrono: string }) {
  const { t } = useT();
  return (
    <div className={cx('agx-scribe-chrono', etat.pause && 'agx-scribe-chrono-pause')}>
      <span className="agx-scribe-point" aria-hidden="true" />
      <strong className="ag-mono">{chrono}</strong>
      <span>
        {etat.horsLigne ? t('outils.scribe.panneau.horsLigne', { n: etat.enAttente })
          : t(etat.pause ? 'outils.scribe.panneau.enPause' : 'outils.scribe.panneau.prendNotes')}
      </span>
    </div>
  );
}

/**
 * Enregistrement dans un autre onglet (état diffusé par celui-ci) : titre, durée et état ; « Arrêter l'enregistrement »,
 * après confirmation, est transmis à l'onglet qui enregistre (il s'arrête comme si l'on avait cliqué chez lui, puis
 * envoie la réunion au traitement). L'enregistrement ne change jamais d'onglet.
 */
function EnregistrementAilleurs({ autre, chrono, arreter }: { autre: EtatScribe; chrono: string; arreter: () => void }) {
  const { t } = useT();
  const confirmer = useConfirmation();
  const titre = autre.reunion?.titre ?? '';
  const demanderArret = async () => {
    if (await confirmer({
      titre: t('outils.scribe.panneau.autreOnglet.arreterTitre'), texte: t('outils.scribe.panneau.autreOnglet.arreterTexte', { titre }),
      confirmer: t('outils.scribe.panneau.arreter'), danger: true,
    })) arreter();
  };
  return (
    <>
      <p><strong>{titre}</strong></p>
      <LigneChrono etat={autre} chrono={chrono} />
      <p className="agx-vide">{t('outils.scribe.panneau.autreOnglet.aide')}</p>
      <div className="agx-scribe-actions">
        <Button variant="danger" onClick={() => void demanderArret()}>{t('outils.scribe.panneau.arreter')}</Button>
      </div>
    </>
  );
}

/** Avant l'enregistrement : titre, micro, rappel et case « Participants informés », plafond de la semaine. */
function Demarrage({ s }: { s: ContexteScribe }) {
  const { t, langue, locale } = useT();
  const f = useFormats();
  const { api } = useSocle();
  const quota = useQuery({
    queryKey: ['scribe', 'quota'],
    queryFn: ({ signal }) => api.get<QuotaScribe>('/v1/outils/scribe/quota', undefined, signal),
    staleTime: 30_000,
  });
  const [titreDefaut] = React.useState(() => t('outils.scribe.panneau.titreDefaut', { date: f.dateHeure(new Date()) }));
  // Réunion Outlook choisie (« Utiliser ») : ses invités deviennent les participants, modifiables dans la section
  // repliable, et sont joints à la réunion démarrée (compte-rendu, destinataires du brouillon d'e-mail).
  const [choisie, setChoisie] = React.useState(false);
  const [participants, setParticipants] = React.useState<InviteReunion[]>([]);
  const formulaire = useFormulaire({
    schema: schemaReunionNouvelle,
    valeurs: { titre: titreDefaut, niveau: NIVEAU_COMPTE_RENDU_DEFAUT, participantsInformes: false },
    // Langue du compte-rendu : celle d'AGORA de l'utilisateur au démarrage. Participants (invités de la réunion Outlook
    // choisie, modifiés ou non) ; titre encore celui proposé ici : l'IA en proposera un (titreAuto).
    preparer: (v) => ({
      ...v, langue,
      ...(participants.length ? { invites: participants.filter((p) => ADRESSE.test(p.email)).slice(0, BORNES_SCRIBE.invites) } : {}),
      ...(v.titre.trim() === titreDefaut ? { titreAuto: true } : {}),
    }),
    envoyer: (corps) => s.demarrer(corps),
    messages: { participantsInformes: t('outils.scribe.panneau.participantsRequis') },
  });
  const utiliser = (e: EvenementAgenda) => {
    setChoisie(true);
    setParticipants(e.invites.map(({ nom, email }) => ({ nom, email })));
    if (e.sujet) formulaire.modifier('titre', e.sujet);
  };
  const atteint = !!quota.data && quota.data.utiliseS >= quota.data.plafondS;
  const idNiveau = React.useId();
  const erreurInformes = formulaire.erreurDe('participantsInformes');
  let nomLangue: string = langue;
  try { nomLangue = new Intl.DisplayNames([locale], { type: 'language' }).of(langue) ?? langue; } catch { /* nom par défaut */ }

  return (
    <Formulaire formulaire={formulaire} className="agx-scribe-formulaire">
      {choisie ? null : <ReunionsOutlook utiliser={utiliser} />}
      <TextField label={t('outils.scribe.panneau.titreReunion')} {...formulaire.champ('titre')} maxLength={BORNES_SCRIBE.titre} />
      <ParticipantsReunion valeur={participants} modifier={setParticipants} />
      <div className="ag-field" role="group" aria-labelledby={idNiveau}>
        <label id={idNiveau}>{t('outils.scribe.fiche.niveau')}</label>
        <SegmentedControl
          ariaLabel={t('outils.scribe.fiche.niveau')} {...formulaire.champ('niveau')}
          options={NIVEAUX_COMPTE_RENDU.map((n) => ({ value: n, label: t(`outils.scribe.fiche.niveaux.${n}`) }))}
        />
        <small>{t(`outils.scribe.panneau.niveaux.${formulaire.valeur('niveau') ?? NIVEAU_COMPTE_RENDU_DEFAUT}`)}</small>
      </div>
      <ChoixMicro s={s} />
      <OptionDistance s={s} />
      <p className="agx-vide">{t('outils.scribe.panneau.rappel')}</p>
      <Toggle label={t('outils.scribe.panneau.participantsInformes')} {...formulaire.interrupteur('participantsInformes')} />
      {erreurInformes ? <p className="agx-scribe-erreur" role="alert">{erreurInformes}</p> : null}
      {quota.isLoading ? (
        <SkeletonGroup label={t('app.chargement')}><Skeleton width={70} /></SkeletonGroup>
      ) : quota.data ? (
        <p className="agx-vide">{t('outils.scribe.quota', { utilise: dureeLisible(quota.data.utiliseS, f.nombre), plafond: dureeLisible(quota.data.plafondS, f.nombre) })}</p>
      ) : null}
      {atteint ? <p className="agx-scribe-erreur" role="alert">{t('outils.scribe.quotaAtteint')}</p> : null}
      <p className="agx-vide">{t('outils.scribe.panneau.langueCompteRendu', { langue: nomLangue })}</p>
      <div className="agx-scribe-actions">
        <Button type="submit" variant="primary" icon="micro" disabled={formulaire.envoiEnCours || atteint || !s.micro.flux || attenteDistance(s)}>
          {t('outils.scribe.panneau.demarrer')}
        </Button>
      </div>
    </Formulaire>
  );
}

/**
 * Participants de la réunion, dans une section repliable (fermée par défaut, chevron) : au départ les invités de la
 * réunion Outlook utilisée, puis ajoutés (adresse saisie ou suggérée) ou retirés ; « Participants (n) » dans l'en-tête.
 * Le corps reste dans le DOM (masqué) pour que aria-controls désigne toujours un élément.
 */
function ParticipantsReunion({ valeur, modifier }: { valeur: InviteReunion[]; modifier: (p: InviteReunion[]) => void }) {
  const { t } = useT();
  const [ouvert, setOuvert] = React.useState(false);
  const id = React.useId();
  return (
    <div className="agx-scribe-participants">
      <button type="button" className="agx-scribe-repli" aria-expanded={ouvert} aria-controls={id} onClick={() => setOuvert((o) => !o)}>
        <Icon name="chevron" size={16} className={cx('agx-scribe-repli-chevron', ouvert && 'agx-scribe-repli-ouvert')} />
        <span>{t('outils.scribe.panneau.participants.titre', { n: valeur.length })}</span>
      </button>
      <div id={id} hidden={!ouvert}>
        {ouvert ? (
          <React.Suspense fallback={<SkeletonGroup label={t('app.chargement')}><Skeleton width={100} /></SkeletonGroup>}>
            <ChampParticipants valeur={valeur} modifier={modifier} />
          </React.Suspense>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Réunions de l'agenda Outlook en cours ou qui commencent dans les 15 minutes (GET /v1/outils/scribe/agenda, lu à
 * l'ouverture du panneau) : une ligne par réunion, « Réunion Outlook : sujet (10:30 – 11:30, participants : n) » et
 * « Utiliser » ; une fois une réunion utilisée, le bloc disparaît (demande du DSI, 03/11/2026 : le titre suffit ; fermer
 * le panneau avant le démarrage annule le choix). Sans messagerie connectée, sans réunion ou en erreur : rien ;
 * connexion sans accès à l'agenda : rappel discret de reconnecter la messagerie.
 */
function ReunionsOutlook({ utiliser }: { utiliser: (e: EvenementAgenda) => void }) {
  const { t } = useT();
  const f = useFormats();
  const { api } = useSocle();
  const agenda = useQuery({
    queryKey: ['scribe', 'agenda'],
    queryFn: ({ signal }) => api.get<AgendaScribe>('/v1/outils/scribe/agenda', undefined, signal),
    staleTime: 60_000,
    retry: false,
  });
  const sujet = (e: EvenementAgenda) => e.sujet || t('outils.scribe.panneau.agenda.sansSujet');
  const etat = agenda.data?.etat;
  const evenements = etat === 'disponible' && Array.isArray(agenda.data?.evenements) ? agenda.data.evenements : [];
  if (etat === 'agenda_inaccessible') return <p className="agx-vide">{t('outils.scribe.panneau.agenda.inaccessible')}</p>;
  if (!evenements.length) return null;
  const utiliserLibelle = t('outils.scribe.panneau.agenda.utiliser');
  return (
    <ul className="agx-scribe-outlook-liste" aria-label={t('outils.scribe.panneau.agenda.titre')}>
      {evenements.map((e) => (
        <li key={e.id} className="agx-scribe-outlook">
          <span>{t('outils.scribe.panneau.agenda.evenement', { sujet: sujet(e), debut: f.heure(e.debut), fin: f.heure(e.fin), n: e.invites.length })}</span>
          <Button size="sm" aria-label={`${utiliserLibelle} · ${sujet(e)}`} onClick={() => utiliser(e)}>{utiliserLibelle}</Button>
        </li>
      ))}
    </ul>
  );
}

/** Option « Participants à distance » choisie sans son partagé : démarrer ou reprendre attend le partage. */
const attenteDistance = (s: ContexteScribe) => !!s.distance?.actif && !s.distance.partage;

/**
 * Option « Participants à distance (son de Teams) » (Edge, Chrome) : interrupteur, consigne (onglet de Teams ou écran
 * entier, « Partager l'audio » coché), « Partager le son de Teams » puis l'état du partage (« Arrêter le partage »).
 * Pendant l'enregistrement (`pendant`) : seulement si l'option est choisie – état du partage, ou « Partager de nouveau ».
 */
function OptionDistance({ s, pendant = false }: { s: ContexteScribe; pendant?: boolean }) {
  const { t } = useT();
  const d = s.distance;
  if (!d || (pendant && !d.actif && !d.partage)) return null;
  const etatPartage = d.partage ? (
    <div className="agx-scribe-partage" role="status">
      <span>{t('outils.scribe.panneau.distance.partage')}</span>
      <Button size="sm" variant="quiet" onClick={d.arreter}>{t('outils.scribe.panneau.distance.arreter')}</Button>
    </div>
  ) : (
    <Button size="sm" icon="share" onClick={() => void d.partager()}>
      {t(pendant || s.etat.reunion?.distance ? 'outils.scribe.panneau.distance.partagerDeNouveau' : 'outils.scribe.panneau.distance.partager')}
    </Button>
  );
  return (
    <div className="agx-scribe-distance">
      {pendant ? null : (
        <Toggle label={t('outils.scribe.panneau.distance.option')} help={t('outils.scribe.panneau.distance.aide')} checked={d.actif} onChange={d.activer} />
      )}
      {d.actif || d.partage ? (
        <>
          {!d.partage && s.etat.phase === 'a-reprendre' && s.etat.reunion?.distance ? <p className="agx-vide">{t('outils.scribe.panneau.distance.aRefaire')}</p> : null}
          {etatPartage}
          {d.erreur ? <p className="agx-scribe-erreur" role="alert">{t(`outils.scribe.panneau.distance.erreurs.${d.erreur}`)}</p> : null}
        </>
      ) : null}
    </div>
  );
}

/** Choix du micro (s'il y en a plusieurs) et niveau capté ; micro refusé ou absent : message et « Réessayer ». */
function ChoixMicro({ s }: { s: ContexteScribe }) {
  const { t } = useT();
  const { micro } = s;
  if (micro.erreur) {
    return (
      <div className="agx-scribe-micro">
        <p className="agx-scribe-erreur" role="alert">{t(micro.erreur === 'refuse' ? 'outils.scribe.panneau.microRefuse' : 'outils.scribe.panneau.microIndisponible')}</p>
        <Button size="sm" onClick={() => void micro.ouvrir()}>{t('commun.reessayer')}</Button>
      </div>
    );
  }
  return (
    <div className="agx-scribe-micro">
      {micro.appareils.length ? (
        <SelectField
          label={t('outils.scribe.panneau.micro')} value={micro.choisi}
          options={micro.appareils.map((a, i) => ({ value: a.id, label: a.libelle || t('outils.scribe.panneau.microN', { n: i + 1 }) }))}
          onChange={(id) => void micro.ouvrir(id)}
        />
      ) : null}
      <Vumetre flux={micro.flux} />
    </div>
  );
}

/**
 * Niveau du micro : barre (rôle « meter ») dont la longueur suit le volume capté, mesuré 10 fois par seconde
 * (AnalyserNode). Ce n'est pas une animation : la longueur change à chaque mesure, sans transition ; rien ne change si
 * l'utilisateur a réduit les animations.
 */
export function Vumetre({ flux }: { flux: MediaStream | null }) {
  const { t } = useT();
  const niveau = useNiveau(flux);
  const pct = Math.round(niveau * 100);
  return (
    <div className="agx-scribe-niveau" role="meter" aria-label={t('outils.scribe.panneau.niveau')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <span style={{ '--agx-niveau': `${pct}%` } as React.CSSProperties} />
    </div>
  );
}

function useNiveau(flux: MediaStream | null): number {
  const [niveau, setNiveau] = React.useState(0);
  React.useEffect(() => {
    const Audio = typeof window !== 'undefined' ? window.AudioContext : undefined;
    if (!flux || !Audio || !flux.getAudioTracks().some((p) => p.readyState === 'live')) { setNiveau(0); return; }
    let ctx: AudioContext;
    try { ctx = new Audio(); } catch { return; }
    void ctx.resume().catch(() => undefined);
    const source = ctx.createMediaStreamSource(flux);
    const analyse = ctx.createAnalyser();
    analyse.fftSize = 1024;
    source.connect(analyse);
    const mesures = new Uint8Array(analyse.fftSize);
    const id = window.setInterval(() => {
      analyse.getByteTimeDomainData(mesures);
      let somme = 0;
      for (const v of mesures) { const x = (v - 128) / 128; somme += x * x; }
      // Parole ≈ 0,05 à 0,2 de valeur efficace : × 4 pour une barre lisible.
      setNiveau(Math.min(1, Math.sqrt(somme / mesures.length) * 4));
    }, MESURE_NIVEAU_MS);
    return () => {
      window.clearInterval(id);
      source.disconnect();
      void ctx.close().catch(() => undefined);
    };
  }, [flux]);
  return niveau;
}
